import { OverlayProvider } from "@components/contexts/overlayProvider";
import { ThemeProvider } from "@components/contexts/themeProvider";
import GlobalFormModal from "@components/GlobalFormModal";
import { processEnv } from "@lib/processEnv";
import "@styles/colors.css";
import "@styles/common.css";
import "@styles/dark-mode.css";
import "@styles/emojipickercustom.css";
import "@styles/fonts.css";
import "@styles/index.css";
import "@styles/landing-animation.css";
import "@styles/prism.css";
import "@styles/screen-fixings.css";
import "@styles/screen.css";
import "@styles/toc.css";
import "@styles/emojipickercustom.css";
import "@styles/style-min.css";
import "@styles/webinar.css";
import "@styles/fader.css";
import "@styles/lineloader.css";
import { urlTracker, refTracker } from "@utils/campaign-tracker";
import { AppProps } from "next/app";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import SnackbarProvider from "react-simple-snackbar";
import { pushUserIdToGTM } from "@utils/UserTracker";
import * as prdAnalyticsTracker from "@utils/amplitudeTracker";
import { utmTracker } from "@utils/utmTracking";
import { initMixpanel, trackPageView } from "@utils/mixpanelClient";

const SESSION_STORAGE_KEY = "globalFormPopupShown";
const POPUP_DELAY_MS = 10000; // 10 seconds delay

const App: React.FC<AppProps> = ({ Component, pageProps }) => {
    const router = useRouter();

    const [showGlobalFormModal, setShowGlobalFormModal] = useState<boolean>(false);

    useEffect(() => {
        const { cmsData } = pageProps || { cmsData: null };
        const { bodyClass } = cmsData || { bodyClass: null };
        const htmlTag = document.querySelector("html") as HTMLElement;
        htmlTag.className = bodyClass ? "casper" : "home";
    });

    prdAnalyticsTracker.useInit({
        apiKey: process.env.NEXT_PUBLIC_AMPLITUDE_DEVELOPMENT_API_KEY as string,
    });

    useEffect(() => {
        urlTracker();
        refTracker();
        const userId = pushUserIdToGTM();
        const t = setTimeout(() => {
            prdAnalyticsTracker.setUser(userId);
        }, 100);

        return () => {
            clearTimeout(t);
            prdAnalyticsTracker.disconnect();
        };
    }, []);

    useEffect(() => {
        utmTracker();
    }, [router.asPath]);

    useEffect(() => {
        initMixpanel();

        const handleRouteChange = (url: string) => {
            trackPageView(url);
        };

        // Track page views on route change
        router.events.on("routeChangeComplete", handleRouteChange);

        return () => {
            router.events.off("routeChangeComplete", handleRouteChange);
        };
    }, [router.events]);

    // Global form popup logic
    useEffect(() => {
        if (typeof window === "undefined") return;

        const currentPath = router.pathname || router.asPath;
        const allowedRoutes = [
            "/whatsapp-chatbot",
            "/click-to-whatsapp-ads",
            "/whatsapp-drip-marketing",
            "/whatsapp-shared-inbox",
            "/whatsapp-integrations",
            "/whatsapp-business-api",
            "/whatsapp-automation",
            "/whatsapp-marketing",
            "/whatsapp-conversation-pricing-calculator",
            "/whatsapp-flows",
            "/whatsapp-qr-code-generator",
            "/whatsapp-link-generator",
            "/whatsapp-chat-button",
            "/solutions/customer-support",
            "/solutions/enterprise",
            "/solutions/marketing-teams",
            "/solutions/sales-team",
            "/solutions/small-medium-business",
            "/industry/education/whatsapp",
            "/industry/travel/whatsapp",
            "/industry/finance/whatsapp",
            "/industry/logistics-transportation/whatsapp",
            "/industry/real-estate/whatsapp",
            "/industry/healthcare/whatsapp",
            "/industry/professional-services/whatsapp",
            "/industry/automotive/whatsapp",
        ];
        if (!allowedRoutes.includes(currentPath)) {
            return;
        }

        const timer = setTimeout(() => {
            setShowGlobalFormModal(true);
            sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
        }, POPUP_DELAY_MS);

        return () => {
            clearTimeout(timer);
        };
    }, [router.pathname, router.asPath]);

    const handleGlobalFormClose = () => {
        setShowGlobalFormModal(false);
    };

    const handleGlobalFormSuccess = () => {
        setShowGlobalFormModal(false);
    };

    return (
        <ThemeProvider {...processEnv.darkMode}>
            <OverlayProvider>
                <SnackbarProvider>
                    <Component {...pageProps} />
                </SnackbarProvider>
            </OverlayProvider>
            {showGlobalFormModal && <GlobalFormModal onClose={handleGlobalFormClose} onSuccess={handleGlobalFormSuccess} />}
        </ThemeProvider>
    );
};

export default App;
